/**
 * Coupon redemption limits (DEV-14248).
 *
 * Shared form model, validation and copy for the Order/Ticket Redemption Limit fields rendered by
 * the Create Coupon modal and the Edit Coupon Details page. Both surfaces are legacy JS controllers,
 * so they reach this module through `CouponRedemptionLimitsService` rather than importing it.
 *
 * The coupons API (acme-core PR #8886) treats `null` as "no limit" and rejects anything outside
 * 1..100000 with error 2062; an update whose order limit is below the orders already redeemed is
 * rejected with error 2063. `orderRedemptionCount` and `redemptionStatus` are server-owned.
 */

export const COUPON_REDEMPTION_LIMIT_FLAG = 'coupons_redemption_limit';

/** acme-core `COUPON_REDEMPTION_LIMIT_INVALID`: limits must be empty or between 1 and 100000. */
export const REDEMPTION_LIMIT_MIN = 1;
export const REDEMPTION_LIMIT_MAX = 100000;

/** acme-core error codes surfaced by the coupon write endpoints. */
export const API_ERROR_REDEMPTION_LIMIT_INVALID = 2062;
export const API_ERROR_ORDER_LIMIT_BELOW_COUNT = 2063;

export const ORDER_REDEMPTION_LIMIT_LABEL = 'Order Redemption Limit';
export const TICKET_REDEMPTION_LIMIT_LABEL = 'Ticket Redemption Limit';
export const ORDER_REDEMPTION_COUNT_LABEL = 'Order Redemption Count';

export type RedemptionLimitKey = 'order' | 'ticket';

export interface RedemptionLimitField {
  /** Checkbox state. Unchecked means "no limit" and is saved as `null`. */
  enabled: boolean;
  /** Raw text typed into the limit input; kept as text so "abc" is reported as invalid, not blank. */
  value: string;
}

export type RedemptionLimitsForm = Record<RedemptionLimitKey, RedemptionLimitField>;

export type RedemptionLimitsErrors = Partial<Record<RedemptionLimitKey, string>>;

export interface RedemptionLimitsPayload {
  orderRedemptionLimit: number | null;
  ticketRedemptionLimit: number | null;
}

export interface RedemptionLimitsValidation {
  isValid: boolean;
  /** Red banner at the top of the form ("Please enter a value for …"). */
  bannerMessages: string[];
  fieldErrors: RedemptionLimitsErrors;
  payload: RedemptionLimitsPayload;
}

export interface RedemptionLimitsContext {
  orderRedemptionCount?: number | string | null;
}

export interface CouponRedemptionLimitsSource {
  orderRedemptionLimit?: number | null;
  ticketRedemptionLimit?: number | null;
}

export interface RedemptionLimitCopy {
  label: string;
  helpText: string;
}

export const REDEMPTION_LIMIT_COPY: Record<RedemptionLimitKey, RedemptionLimitCopy> = {
  order: {
    label: ORDER_REDEMPTION_LIMIT_LABEL,
    helpText: 'Limit how many orders this code can be used on.',
  },
  ticket: {
    label: TICKET_REDEMPTION_LIMIT_LABEL,
    helpText: 'Limit how many tickets per order this code can be used on.',
  },
};

export const REDEMPTION_LIMIT_HINT = 'Leave unchecked for no limit.';

export function missingRedemptionLimitMessage(label: string): string {
  return `Please enter a value for ${label}.`;
}

export function invalidRedemptionLimitMessage(label: string): string {
  return `${label} is invalid.`;
}

export function orderLimitBelowCountMessage(orderRedemptionCount?: number | string | null): string {
  const count = toCount(orderRedemptionCount);
  const suffix = count === null ? '' : ` ${count}`;
  return `Value for ${ORDER_REDEMPTION_LIMIT_LABEL} cannot be set below current value for ${ORDER_REDEMPTION_COUNT_LABEL}${suffix}.`;
}

export function createRedemptionLimitsForm(coupon?: CouponRedemptionLimitsSource | null): RedemptionLimitsForm {
  return {
    order: toField(coupon?.orderRedemptionLimit),
    ticket: toField(coupon?.ticketRedemptionLimit),
  };
}

export type ParsedRedemptionLimit = { kind: 'empty' } | { kind: 'invalid' } | { kind: 'valid'; value: number };

export function parseRedemptionLimit(raw: unknown): ParsedRedemptionLimit {
  if (raw === null || raw === undefined) {
    return { kind: 'empty' };
  }

  const text = String(raw).trim();
  if (text === '') {
    return { kind: 'empty' };
  }

  if (!/^\d+$/.test(text)) {
    return { kind: 'invalid' };
  }

  const value = Number(text);
  if (value < REDEMPTION_LIMIT_MIN || value > REDEMPTION_LIMIT_MAX) {
    return { kind: 'invalid' };
  }

  return { kind: 'valid', value };
}

export function validateRedemptionLimits(
  form: RedemptionLimitsForm,
  context: RedemptionLimitsContext = {},
): RedemptionLimitsValidation {
  const bannerMessages: string[] = [];
  const fieldErrors: RedemptionLimitsErrors = {};
  const payload: RedemptionLimitsPayload = { orderRedemptionLimit: null, ticketRedemptionLimit: null };

  (['order', 'ticket'] as RedemptionLimitKey[]).forEach((key) => {
    const field = form[key];
    const label = REDEMPTION_LIMIT_COPY[key].label;
    if (!field || !field.enabled) {
      return;
    }

    const parsed = parseRedemptionLimit(field.value);
    if (parsed.kind === 'empty') {
      bannerMessages.push(missingRedemptionLimitMessage(label));
      return;
    }
    if (parsed.kind === 'invalid') {
      fieldErrors[key] = invalidRedemptionLimitMessage(label);
      return;
    }

    if (key === 'order') {
      const count = toCount(context.orderRedemptionCount);
      if (count !== null && parsed.value < count) {
        fieldErrors.order = orderLimitBelowCountMessage(count);
        return;
      }
      payload.orderRedemptionLimit = parsed.value;
    } else {
      payload.ticketRedemptionLimit = parsed.value;
    }
  });

  return {
    isValid: bannerMessages.length === 0 && Object.keys(fieldErrors).length === 0,
    bannerMessages,
    fieldErrors,
    payload,
  };
}

export function formatOrderRedemptionCount(
  orderRedemptionCount?: number | string | null,
  orderRedemptionLimit?: number | string | null,
): string {
  const count = toCount(orderRedemptionCount);
  if (count === null) {
    return '-';
  }

  const limit = toCount(orderRedemptionLimit);
  return limit === null ? `${count} (no limit)` : `${count} of ${limit}`;
}

export interface SaveErrorResponse {
  status?: number;
  data?: { errorCode?: number; message?: string; error?: string } | null;
}

export interface SaveErrorDescription {
  message: string;
  fieldErrors: RedemptionLimitsErrors;
  /** True when the server rejected the order limit for being below the redeemed count (error 2063). */
  isOrderLimitBelowCount: boolean;
}

const DEFAULT_SAVE_ERROR = 'An error occurred while saving the coupon.';

export function describeCouponSaveError(
  response: SaveErrorResponse | null | undefined,
  context: RedemptionLimitsContext = {},
): SaveErrorDescription {
  const data = response?.data ?? undefined;
  const fallback = data?.message || data?.error || DEFAULT_SAVE_ERROR;

  if (data?.errorCode === API_ERROR_ORDER_LIMIT_BELOW_COUNT) {
    const message = orderLimitBelowCountMessage(context.orderRedemptionCount);
    return { message, fieldErrors: { order: message }, isOrderLimitBelowCount: true };
  }

  return { message: fallback, fieldErrors: {}, isOrderLimitBelowCount: false };
}

/** Pendo events (DEV-14261), sent only on a successful save while the redemption limit flag is on. */
export const COUPON_CREATE_SAVED_EVENT = 'ACME - Coupons | Create Coupon - Saved';
export const COUPON_EDIT_SAVED_EVENT = 'ACME - Coupons | Edit Coupon - Saved';

/** Which Edit Coupon Details control saved the coupon: either Save & Close, Save & Continue, or a step tab. */
export type CouponEditSaveAction = 'save_and_close' | 'save_and_continue' | 'tab_change';

export interface RedemptionLimitsTrackingProperties {
  has_order_redemption_limit: boolean;
  order_redemption_limit_count: number | null;
  has_ticket_redemption_limit: boolean;
  ticket_redemption_limit_count: number | null;
}

export function buildRedemptionLimitsTrackingProperties(
  limits: CouponRedemptionLimitsSource,
): RedemptionLimitsTrackingProperties {
  const order = limits.orderRedemptionLimit ?? null;
  const ticket = limits.ticketRedemptionLimit ?? null;
  return {
    has_order_redemption_limit: order !== null,
    order_redemption_limit_count: order,
    has_ticket_redemption_limit: ticket !== null,
    ticket_redemption_limit_count: ticket,
  };
}

function toField(limit?: number | null): RedemptionLimitField {
  const hasLimit = limit !== null && limit !== undefined;
  return { enabled: hasLimit, value: hasLimit ? String(limit) : '' };
}

function toCount(value?: number | string | null): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
}
